import * as React from "react"
import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"

type Account = {
  id: number
  code: string | null
  name: string
  outlet_group_key: string
  channel_group: string | null
  is_key_account: boolean
  research_enabled: boolean
  status: string
  account_group_id: number | null
  pic_employee_id: number | null
}

type PageProps = {
  account: Account
  account_groups: { id: number; name: string }[]
  channel_groups: string[]
  suggested_channel_sub: string | null
  employees: { id: number; name: string; job_title: string }[]
  statuses: string[]
}

export default function Edit({
  account,
  account_groups,
  channel_groups,
  suggested_channel_sub,
  employees,
  statuses,
}: PageProps) {
  // P3: the form key + DB column stay `channel_group`, but the field is a Channel
  // Sub sourced from master outlet. When the account has no channel sub set yet and
  // its outlets resolve to exactly one, pre-select that suggestion so the admin just
  // saves to persist it.
  const { data, setData, patch, processing, errors } = useForm({
    code: account.code ?? "",
    name: account.name ?? "",
    account_group_id: account.account_group_id ? String(account.account_group_id) : "",
    channel_group: account.channel_group || suggested_channel_sub || "",
    pic_employee_id: account.pic_employee_id ? String(account.pic_employee_id) : "",
    pic_note: "",
    pic_effective_on: "",
    status: account.status,
    research_enabled: account.research_enabled,
  })

  // Effective-date control (#44 P1). The two helper dates ("1st of this month" and
  // "today") are computed on mount — never during render — so SSR stays deterministic
  // and there's no hydration mismatch. Until they fill, pic_effective_on is "" and the
  // server safely falls back to "now" (= pre-patch behavior).
  const [picEffectiveMode, setPicEffectiveMode] = React.useState<"month_start" | "specific">(
    "month_start",
  )
  const [monthStart, setMonthStart] = React.useState("")
  const [maxDate, setMaxDate] = React.useState("")

  React.useEffect(() => {
    const now = new Date()
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    setMonthStart(`${ym}-01`)
    setMaxDate(`${ym}-${String(now.getDate()).padStart(2, "0")}`)
    setData("pic_effective_on", `${ym}-01`)
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onEffectiveModeChange = (mode: "month_start" | "specific") => {
    setPicEffectiveMode(mode)
    setData("pic_effective_on", mode === "month_start" ? monthStart : maxDate)
  }

  const description = `Kurasi identitas akun ${account.name}: kode, nama, holding, channel sub, PIC, status, dan toggle riset.`

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/accounts/${account.id}`)
  }

  return (
    <>
      <Head title={`Edit ${account.name}`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`Edit ${account.name}`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts" className="hover:text-ink-body">
            Account
          </Link>
          <span>/</span>
          <Link href={`/admin/accounts/${account.id}`} className="hover:text-ink-body">
            {account.name}
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Edit</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Edit identitas akun</h1>
          <p className="mt-1 text-sm text-ink-muted">{description}</p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 max-w-2xl">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name">Nama akun</label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                className="mt-1"
              />
              {errors.name && <p className="mt-1 text-sm text-danger">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="code">Kode</label>
              <Input
                id="code"
                value={data.code}
                onChange={(e) => setData("code", e.target.value)}
                placeholder="cth. SAT, IDM"
                className="mt-1"
              />
              {errors.code && <p className="mt-1 text-sm text-danger">{errors.code}</p>}
            </div>

            <div>
              <label htmlFor="channel_group">Channel Sub</label>
              <Select
                id="channel_group"
                className="mt-1"
                value={data.channel_group}
                onChange={(e) => setData("channel_group", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {channel_groups.map((cg) => (
                  <option key={cg} value={cg}>
                    {cg}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="account_group_id">Holding / Grup</label>
              <Select
                id="account_group_id"
                className="mt-1"
                value={data.account_group_id}
                onChange={(e) => setData("account_group_id", e.target.value)}
              >
                <option value="">— Tidak ada —</option>
                {account_groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="pic_employee_id">PIC</label>
              <Select
                id="pic_employee_id"
                className="mt-1"
                value={data.pic_employee_id}
                onChange={(e) => setData("pic_employee_id", e.target.value)}
              >
                <option value="">— Belum ditetapkan —</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} · {emp.job_title}
                  </option>
                ))}
              </Select>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="pic_note">Catatan penugasan (opsional)</label>
              <textarea
                id="pic_note"
                value={data.pic_note}
                onChange={(e) => setData("pic_note", e.target.value)}
                rows={2}
                className="form-control mt-1"
                placeholder="cth. Handover Q3, reshuffle territory"
              />
              <p className="mt-1 text-xs text-ink-muted">
                Hanya dicatat saat PIC diganti, dan tampil di Riwayat PIC.
              </p>
            </div>

            {data.pic_employee_id && (
              <div className="sm:col-span-2">
                <span className="block text-sm font-medium text-ink-display">Tanggal efektif</span>
                <RadioGroup className="mt-2">
                  <label className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="pic_effective_mode"
                      value="month_start"
                      checked={picEffectiveMode === "month_start"}
                      onChange={() => onEffectiveModeChange("month_start")}
                    />
                    Awal bulan ini
                  </label>
                  <label className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="pic_effective_mode"
                      value="specific"
                      checked={picEffectiveMode === "specific"}
                      onChange={() => onEffectiveModeChange("specific")}
                    />
                    Tanggal spesifik
                  </label>
                </RadioGroup>

                {picEffectiveMode === "specific" && (
                  <Input
                    id="pic_effective_on"
                    type="date"
                    max={maxDate}
                    value={data.pic_effective_on}
                    onChange={(e) => setData("pic_effective_on", e.target.value)}
                    className="mt-2 max-w-xs"
                  />
                )}

                <p className="mt-1 text-xs text-ink-muted">
                  Tanggal mulai PIC baru menjabat. Default awal bulan ini; tidak bisa tanggal yang
                  akan datang.
                </p>
                {errors.pic_effective_on && (
                  <p className="mt-1 text-sm text-danger">{errors.pic_effective_on}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="status">Status</label>
              <Select
                id="status"
                className="mt-1"
                value={data.status}
                onChange={(e) => setData("status", e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s === "archived" ? "Diarsipkan" : "Aktif"}
                  </option>
                ))}
              </Select>
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={account.is_key_account ? true : data.research_enabled}
                  disabled={account.is_key_account}
                  onChange={(e) => setData("research_enabled", e.target.checked)}
                />
                Aktifkan riset 360°
              </label>
              {account.is_key_account && (
                <p className="mt-1 text-xs text-ink-muted">
                  Akun ini adalah Key Account — riset selalu aktif dan tidak dapat dimatikan.
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3 border-t border-hairline pt-6">
            <Button type="submit" disabled={processing}>
              Simpan perubahan
            </Button>
            <Button asChild variant="ghost">
              <Link href={`/admin/accounts/${account.id}`}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Batal
              </Link>
            </Button>
          </div>
        </form>
      </AppShell>
    </>
  )
}
